import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  CriarSenhaResponse,
  OtpEnvelope,
  OTP_ERROR_CODES,
  OtpFluxo,
  ReenviarCodigoResponse,
  SolicitarCodigoResponse,
  ValidarCodigoResponse
} from '../models/otp.model';

/** Entrada do passo 1 — exatamente um dos dois campos (ADR-010 D9, spec §4.5). */
export type OtpSolicitarCodigoInput = { identificador: string } | { email: string };

/** Prefixo de rota por fluxo — só isto diverge entre `esqueci-senha` e `primeiro-acesso`. */
const OTP_FLUXO_CONFIG: Record<OtpFluxo, { base: string }> = {
  'esqueci-senha': { base: '/auth/esqueci-senha' },
  'primeiro-acesso': { base: '/api/v1/auth/primeiro-acesso' }
};

/**
 * Camada de API do fluxo OTP (OTP-F-02) — compartilhada entre
 * `esqueci-senha` e `primeiro-acesso`, que têm o MESMO contrato (spec §5),
 * só com prefixo de rota e endpoint final (`redefinir`/`criar-senha`, com
 * nome de campo de senha próprio cada um) diferentes.
 *
 * `desafioId` e `resetToken` vão SEMPRE em corpo de POST — nunca em query
 * string nem em rota (vazam em `Referer`, log de proxy e histórico).
 */
@Injectable({ providedIn: 'root' })
export class OtpAuthService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiBaseUrl;

  private url(fluxo: OtpFluxo, path: string): string {
    return `${this.baseUrl}${OTP_FLUXO_CONFIG[fluxo].base}/${path}`;
  }

  solicitarCodigo(
    fluxo: OtpFluxo,
    entrada: OtpSolicitarCodigoInput
  ): Observable<SolicitarCodigoResponse> {
    return this.http
      .post<OtpEnvelope<SolicitarCodigoResponse>>(this.url(fluxo, 'solicitar-codigo'), entrada)
      .pipe(map((res) => res.conteudo));
  }

  escolherEmpresa(
    fluxo: OtpFluxo,
    desafioId: string,
    empresaRef: string
  ): Observable<SolicitarCodigoResponse> {
    return this.http
      .post<OtpEnvelope<SolicitarCodigoResponse>>(this.url(fluxo, 'solicitar-codigo/empresa'), {
        desafioId,
        empresaRef
      })
      .pipe(map((res) => res.conteudo));
  }

  reenviarCodigo(fluxo: OtpFluxo, desafioId: string): Observable<ReenviarCodigoResponse> {
    return this.http
      .post<OtpEnvelope<ReenviarCodigoResponse>>(this.url(fluxo, 'reenviar-codigo'), { desafioId })
      .pipe(map((res) => res.conteudo));
  }

  validarCodigo(
    fluxo: OtpFluxo,
    desafioId: string,
    codigo: string
  ): Observable<ValidarCodigoResponse> {
    return this.http
      .post<OtpEnvelope<ValidarCodigoResponse>>(this.url(fluxo, 'validar-codigo'), {
        desafioId,
        codigo
      })
      .pipe(map((res) => res.conteudo));
  }

  /** `esqueci-senha`: `POST .../redefinir` com `{ resetToken, novaSenha }`. */
  redefinirSenha(resetToken: string, novaSenha: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(this.url('esqueci-senha', 'redefinir'), {
      resetToken,
      novaSenha
    });
  }

  /** `primeiro-acesso`: `POST .../criar-senha` com `{ resetToken, senha }`. */
  criarSenha(resetToken: string, senha: string): Observable<CriarSenhaResponse> {
    return this.http
      .post<OtpEnvelope<CriarSenhaResponse>>(this.url('primeiro-acesso', 'criar-senha'), {
        resetToken,
        senha
      })
      .pipe(map((res) => res.conteudo));
  }
}

// ── Type guards de erro ──────────────────────────────────────────────────────
// Mesmo desenho F-05 de `DesambiguacaoTokenService`: um código, uma mensagem,
// nunca distinguir sub-casos pelo texto (reabriria oráculo que o backend
// fechou de propósito). A distinção vive só no audit do backend.

function temCodigo(err: unknown, status: number, codigo: string): boolean {
  return (
    err instanceof HttpErrorResponse &&
    err.status === status &&
    (err.error as { codigo?: string } | null)?.codigo === codigo
  );
}

export function isEntradaInvalidaError(err: unknown): boolean {
  return temCodigo(err, 422, OTP_ERROR_CODES.ENTRADA_INVALIDA);
}

export function isOtpEscolhaInvalidaError(err: unknown): boolean {
  return temCodigo(err, 422, OTP_ERROR_CODES.ESCOLHA_INVALIDA);
}

export function isOtpCodigoInvalidoError(err: unknown): boolean {
  return temCodigo(err, 422, OTP_ERROR_CODES.CODIGO_INVALIDO);
}

export function isOtpReenvioIndisponivelError(err: unknown): boolean {
  return temCodigo(err, 422, OTP_ERROR_CODES.REENVIO_INDISPONIVEL);
}

export function isRateLimitError(err: unknown): boolean {
  return err instanceof HttpErrorResponse && err.status === 429;
}

/**
 * Conta já tem senha cadastrada — só o backend emite isto em
 * `primeiro-acesso/solicitar-codigo` (spec §4.6-B). `esqueci-senha` nunca
 * recebe este código; o ramo correspondente no fed (hook
 * `onContaJaPossuiSenha`) nunca dispara lá por construção.
 */
export function isContaJaPossuiSenhaError(err: unknown): boolean {
  return temCodigo(err, 422, OTP_ERROR_CODES.CONTA_JA_POSSUI_SENHA);
}

/**
 * Código conferiu, posse do e-mail provada, mas a conta resolvida não tem
 * acesso ao portal (`acessa_portal = false`, spec §4.6-C). `403`, não
 * `422` — é negativa de autorização, não "não foi possível validar o
 * código". O desafio já foi consumido no backend quando isto chega: não há
 * reenvio possível, só voltar ao passo 1 para começar outro do zero.
 */
export function isAcessoPortalNegadoError(err: unknown): boolean {
  return temCodigo(err, 403, OTP_ERROR_CODES.ACESSO_PORTAL_NEGADO);
}
